import type { ReactNode } from "react";
import styles from "./StatTile.module.css";

export interface StatTileProps {
  label: string;
  /** Null while the query behind it is still in flight. */
  value: string | null;
  /** One line of context: what the number counts, or what it excludes. */
  hint?: string;
  /** A badge beside the label, naming what KIND of figure this is. */
  badge?: ReactNode;
  /**
   * `estimate` draws the tile hatched and dashed-edged, so a figure nobody has
   * contracted for never sits in a row of real money looking like one of them.
   */
  variant?: "default" | "estimate";
}

export function StatTile({
  label,
  value,
  hint,
  badge,
  variant = "default",
}: StatTileProps): ReactNode {
  return (
    // A labelled group, so the value and its hint are announced as belonging to
    // this figure rather than as three loose strings — and so a panel headed
    // "Weighted pipeline" and a tile of the same name stay distinguishable.
    <div
      className={`${styles.tile} ${variant === "estimate" ? styles.estimate : ""}`}
      role="group"
      aria-label={label}
    >
      <div className={styles.labelRow}>
        <div className={styles.label}>{label}</div>
        {badge}
      </div>
      <div className={`${styles.value} ${value === null ? styles.pending : ""} mono`}>
        {value ?? "—"}
      </div>
      {hint !== undefined && <div className={styles.hint}>{hint}</div>}
    </div>
  );
}

export function StatTileRow({ children }: { children: ReactNode }): ReactNode {
  return <div className={styles.row}>{children}</div>;
}
